import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Extracts a TiplyMe card code from a raw QR value (may be a full /pay/CODE URL or bare code).
function parseCode(raw) {
  if (!raw) return "";
  const m = String(raw).match(/(?:PAY|TIP)-[A-Z0-9]+/i);
  if (m) return m[0].toUpperCase();
  const parts = String(raw).split("/").filter(Boolean);
  return (parts[parts.length - 1] || raw).trim().toUpperCase();
}

export default function QrScanner({ onResult, testId = "qr-scanner" }) {
  const regionId = useRef(`qr-${Math.random().toString(36).slice(2)}`);
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");

  const stop = async () => {
    const s = scannerRef.current;
    if (s) {
      try { if (s.isScanning) await s.stop(); } catch {}
      try { await s.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const start = async () => {
    setError("");
    try {
      const scanner = new Html5Qrcode(regionId.current, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (decoded) => { onResult(parseCode(decoded)); stop(); },
        () => {}
      );
      setScanning(true);
    } catch (e) {
      setError("Camera unavailable — enter the code manually below.");
      setScanning(false);
    }
  };

  useEffect(() => () => { stop(); }, []);

  return (
    <div data-testid={testId} className="space-y-3">
      <div id={regionId.current} className="overflow-hidden rounded-xl bg-secondary [&_video]:rounded-xl" />
      <div className="flex gap-2">
        {!scanning ? (
          <Button type="button" onClick={start} data-testid="qr-start-camera" className="flex-1 gap-2 rounded-full bg-brand-navy font-semibold text-white hover:bg-brand-navy/90">
            <Camera className="h-4 w-4" /> Scan card with camera
          </Button>
        ) : (
          <Button type="button" variant="outline" onClick={stop} data-testid="qr-stop-camera" className="flex-1 gap-2 rounded-full">
            <CameraOff className="h-4 w-4" /> Stop camera
          </Button>
        )}
      </div>
      {error && <p className="text-xs text-muted-foreground">{error}</p>}
      <div className="flex gap-2">
        <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Or type card code e.g. TIP-AB12CD" data-testid="qr-manual-input" className="uppercase" />
        <Button type="button" variant="secondary" onClick={() => manual && onResult(parseCode(manual))} data-testid="qr-manual-submit" className="rounded-full">Use</Button>
      </div>
    </div>
  );
}
